import type { Supplier, SupplierInput } from '../types'
import { readJson, resetSupplierMemory, STORAGE_KEYS, writeJson } from './storage'
import { createFakeSuppliers } from '../utils/fake-suppliers'

/**
 * SupplierRepository — localStorage persistence mirroring Prisma Supplier.
 */
export class SupplierRepository {
  list(): Supplier[] {
    this.ensureSeed()
    return readJson<Supplier[]>(STORAGE_KEYS.suppliers, 'suppliers', [])
  }

  findById(id: string): Supplier | null {
    return this.list().find((s) => s.id === id) ?? null
  }

  findByCode(code: string): Supplier | null {
    return this.list().find((s) => s.code.toLowerCase() === code.toLowerCase()) ?? null
  }

  create(input: SupplierInput): Supplier {
    const now = new Date().toISOString()
    const code = input.code.trim().toLowerCase()
    if (this.findByCode(code)) throw new Error('Código de fornecedor já existe')
    const supplier: Supplier = {
      id: crypto.randomUUID(),
      name: input.name.trim(),
      code,
      website: input.website ?? null,
      logo: input.logo ?? null,
      supportsApi: Boolean(input.supportsApi),
      supportsAutomation: Boolean(input.supportsAutomation),
      supportsPix: input.supportsPix ?? true,
      supportsInstallments: input.supportsInstallments ?? true,
      active: input.active ?? true,
      kind: input.kind ?? (input.supportsApi && input.supportsAutomation ? 'hybrid' : input.supportsApi ? 'api' : input.supportsAutomation ? 'automation' : 'manual'),
      searchCount: 0,
      createdAt: now,
      updatedAt: now,
    }
    writeJson(STORAGE_KEYS.suppliers, 'suppliers', [supplier, ...this.list()])
    return supplier
  }

  update(id: string, patch: Partial<SupplierInput>): Supplier {
    const current = this.findById(id)
    if (!current) throw new Error('Fornecedor não encontrado')
    const next: Supplier = {
      ...current,
      name: patch.name?.trim() ?? current.name,
      code: patch.code?.trim().toLowerCase() ?? current.code,
      website: patch.website === undefined ? current.website : patch.website,
      logo: patch.logo === undefined ? current.logo : patch.logo,
      supportsApi: patch.supportsApi ?? current.supportsApi,
      supportsAutomation: patch.supportsAutomation ?? current.supportsAutomation,
      supportsPix: patch.supportsPix ?? current.supportsPix,
      supportsInstallments: patch.supportsInstallments ?? current.supportsInstallments,
      active: patch.active ?? current.active,
      kind:
        patch.kind ??
        (patch.supportsApi != null || patch.supportsAutomation != null
          ? deriveKind(patch.supportsApi ?? current.supportsApi, patch.supportsAutomation ?? current.supportsAutomation)
          : current.kind),
      updatedAt: new Date().toISOString(),
    }
    writeJson(
      STORAGE_KEYS.suppliers,
      'suppliers',
      this.list().map((s) => (s.id === id ? next : s)),
    )
    return next
  }

  remove(id: string): void {
    writeJson(
      STORAGE_KEYS.suppliers,
      'suppliers',
      this.list().filter((s) => s.id !== id),
    )
  }

  incrementSearchCount(id: string): void {
    const current = this.findById(id)
    if (!current) return
    this.replace({ ...current, searchCount: current.searchCount + 1, updatedAt: new Date().toISOString() })
  }

  resetMemory(): void {
    resetSupplierMemory()
  }

  private replace(next: Supplier): void {
    writeJson(
      STORAGE_KEYS.suppliers,
      'suppliers',
      this.list().map((s) => (s.id === next.id ? next : s)),
    )
  }

  private ensureSeed(): void {
    const existing = readJson<Supplier[]>(STORAGE_KEYS.suppliers, 'suppliers', [])
    if (existing.length > 0) return
    writeJson(STORAGE_KEYS.suppliers, 'suppliers', createFakeSuppliers())
  }
}

function deriveKind(api: boolean, automation: boolean) {
  if (api && automation) return 'hybrid' as const
  if (api) return 'api' as const
  if (automation) return 'automation' as const
  return 'manual' as const
}

export const supplierRepository = new SupplierRepository()
